/** Extracts the YouTube video ID from common URL formats. */
export function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.slice(1, 12);
      return id.length === 11 ? id : null;
    }
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      const m = u.pathname.match(/^\/(shorts|embed|v)\/([a-zA-Z0-9_-]{11})/);
      return m?.[2] ?? null;
    }
  } catch {
    // ignore invalid URLs
  }
  return null;
}

/** Extracts the Vimeo video ID. */
export function getVimeoId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('vimeo.com')) {
      const m = u.pathname.match(/\/(\d+)/);
      return m?.[1] ?? null;
    }
  } catch {
    // ignore invalid URLs
  }
  return null;
}

export type VideoEmbedInfo =
  | { type: 'youtube'; id: string; embedUrl: string }
  | { type: 'vimeo';   id: string; embedUrl: string }
  | { type: 'file';    embedUrl: string };

export interface VideoEmbedOptions {
  /** Loop the video indefinitely (default: true). */
  loop?: boolean;
}

/**
 * Converts any supported video URL into embed info.
 * Always embeds with audio muted — required for autoplay in all browsers.
 * Unmuting is handled via postMessage after user interaction (see VideoBackground).
 */
export function getVideoEmbedInfo(url: string, options?: VideoEmbedOptions): VideoEmbedInfo | null {
  if (!url?.trim()) return null;

  const loop = options?.loop ?? true;

  const ytId = getYouTubeId(url);
  if (ytId) {
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',           // always start muted for autoplay policy
      enablejsapi: '1',    // required to send postMessage commands (unmute)
      controls: '0',
      showinfo: '0',
      rel: '0',
      modestbranding: '1',
      iv_load_policy: '3',
      playsinline: '1',
    });
    if (loop) { params.set('loop', '1'); params.set('playlist', ytId); }
    return { type: 'youtube', id: ytId, embedUrl: `https://www.youtube.com/embed/${ytId}?${params}` };
  }

  const vimeoId = getVimeoId(url);
  if (vimeoId) {
    const params = new URLSearchParams({
      autoplay: '1',
      muted: '1',          // always start muted for autoplay policy
      background: '1',
    });
    if (loop) params.set('loop', '1');
    return { type: 'vimeo', id: vimeoId, embedUrl: `https://player.vimeo.com/video/${vimeoId}?${params}` };
  }

  // Direct file URL (mp4, webm, etc.)
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) {
    return { type: 'file', embedUrl: url };
  }

  return null;
}

/** Returns true if the URL is a recognized YouTube or Vimeo link. */
export function isEmbedPlatform(url: string): boolean {
  const info = getVideoEmbedInfo(url);
  return info?.type === 'youtube' || info?.type === 'vimeo';
}
